<template>
  <div
    v-if="invoice"
    class="invoice-header d-flex flex-col md:flex-row items-end md:items-center justify-content-between align-items-center mb-3 gap-3"
  >
    <div class="block">
      <div class="flex items-center flex-wrap">
        <slot name="title">
          <h3 class="page-header-2" data-cy="invoice-title">
            Invoice #{{ invoice.invoice_id }}
          </h3>
        </slot>
        <div
          v-if="invoice.qbo_sync_status === 'succeeded'"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <QBSyncBadge :url="invoice.qbo_url" />
        </div>
        <div
          v-if="invoice.exclude_from_batching"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <Badge
            icon="fa-triangle-exclamation"
            class="ml-0"
            color="yellow"
            text="Do Not Batch"
          />
        </div>
        <div class="divider"></div>
        <Badge class="ml-0" v-bind="badgeParams" />
        <div
          v-if="invoice.invoice_last_emailed_at"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <Badge class="ml-0" v-bind="badgeSentParams" />
        </div>
      </div>
    </div>
    <div class="flex actions-wrapper">
      <InvoiceActionsMenu
        class="ml-auto"
        :invoice="invoice"
        :tippy-options="tippyOptions"
        :visible-action-keys="visibleActionKeys"
        has-button-menu
      >
      </InvoiceActionsMenu>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { invoiceBadgeParams, sentBadgeParams } from "@/lib/badgeHelpers.js";
import { EventBus } from "@/lib/event_bus";

import Badge from "@/components/Badge.vue";
import InvoiceActionsMenu from "@/Invoice/InvoiceActionsMenu.vue";
import QBSyncBadge from "@/Quickbooks/QBSyncBadge.vue";

export default {
  components: {
    Badge,
    InvoiceActionsMenu,
    QBSyncBadge
  },
  props: {
    invoice: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      tippyOptions: {
        distance: "10",
        placement: "bottom",
        trigger: "click",
        hideOnClick: true,
        theme: "light"
      },
      visibleActionKeys: [
        "batch",
        "approve",
        "accept_payment",
        "issue_refund",
        "edit_details",
        "edit_date",
        "cancel",
        "download",
        "resubmit_for_payment",
        "email",
        "void",
        "unapprove"
      ]
    };
  },
  computed: {
    ...mapState({
      badgeParams() {
        return invoiceBadgeParams(this.invoice);
      },
      badgeSentParams() {
        return sentBadgeParams(this.invoice.invoice_last_emailed_at);
      }
    })
  },
  mounted() {
    EventBus.$on("payment-method-updated", this.reloadCustomerAndInvoice);
    EventBus.$on("payment-method-removed", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-resubmitted", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-cancelled", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-unapproved", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-voided", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-date-updated", this.reloadCustomerAndInvoice);
    EventBus.$on("invoice-summary-updated", this.reloadCustomerAndInvoice);
  },
  beforeUnmount() {
    EventBus.$off("payment-method-updated", this.reloadCustomerAndInvoice);
    EventBus.$off("payment-method-removed", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-resubmitted", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-cancelled", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-unapproved", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-voided", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-date-updated", this.reloadCustomerAndInvoice);
    EventBus.$off("invoice-summary-updated", this.reloadCustomerAndInvoice);
  },
  methods: {
    ...mapActions(["fetchUser", "fetchInvoice"]),
    reloadCustomerAndInvoice() {
      this.fetchInvoice(this.invoice.invoice_id);
      // this will refresh the "Payment Method" header
      const customerId = this.invoice?.customer?.customer_id;
      if (customerId) {
        this.fetchUser({ id: customerId });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.invoice-header {
  white-space: nowrap;
}
.invoice-badge {
  margin-right: auto;
}
.divider {
  width: 1px;
  margin: 0 12px;
  height: 30px;
  background: var(--sera-color-neutral-gray-med-gray-500);
}
</style>
